/**
 * About — plain-language explainer for the Consumer Trust Wallet.
 *
 * Sections
 *   1. Hero — what the wallet is, in one line
 *   2. What the wallet does — plain-language feature grid
 *   3. How it protects you — consumer-protection commitments
 *   4. Fair Conduct + TCF — the six Treating Customers Fairly outcomes,
 *      explained without jargon.
 *   5. What it is NOT — dispel the "another banking app" misconception
 *
 * Design is deliberately Swiss / high-contrast (BRAND_GUIDELINES.md):
 * Navy + Gold on white, IBM Plex / Outfit via app-level fonts, Lucide
 * icons only — no emoji, no purple gradients.
 */
import { WalletShell, NAVY, NAVY_DARK, GOLD } from "@/components/wallet/WalletShell";
import {
  Info,
  ShieldCheck,
  FileText,
  Users,
  Wallet as WalletIcon,
  Send,
  Building2,
  Compass,
  ClipboardList,
  Sparkles,
  Lock,
  EyeOff,
  ScrollText,
  Ban,
  AlertTriangle,
  Scale,
  BookOpen,
  MessageSquare,
  CheckCircle2,
  Handshake,
} from "lucide-react";
import NaviFace from "@/components/wallet/NaviFace";
import { replayNaviIntro } from "@/components/wallet/NaviIntroModal";
import { T } from "@/contexts/TranslationContext";

export default function WalletAbout() {
  return (
    <WalletShell activeTab="about">
      <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-10" data-testid="wallet-about">
        <Hero />
        <MeetNavi />
        <WhatItDoes />
        <HowItProtects />
        <FairConductTCF />
        <WhatItIsNot />
        <ClosingNote />
      </div>
    </WalletShell>
  );
}

/* ─────────────────────── Meet NAVI ─────────────────────── */
function MeetNavi() {
  return (
    <section
      className="rounded-2xl border border-slate-100 bg-white p-5 lg:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5"
      data-testid="about-meet-navi"
    >
      <NaviFace size="xl" pulse />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
          <T>Meet NAVI</T>
        </div>
        <div className="font-display text-xl lg:text-2xl font-semibold mt-1" style={{ color: NAVY }}>
          <T>Your in-wallet financial explainer.</T>
        </div>
        <p className="text-[13.5px] text-slate-600 mt-1.5 leading-relaxed">
          <T>NAVI reads every document you upload, every quote you receive and every product being offered to you — and she explains it back to you in plain language. She never sells you anything and she never gives advice — she just makes sure you understand what's being asked of you before you say yes.</T>
        </p>
        <button
          type="button"
          onClick={replayNaviIntro}
          data-testid="about-navi-intro-replay"
          className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold text-white transition hover:opacity-95"
          style={{ background: NAVY }}
        >
          <T>Watch her hello again</T>
        </button>
      </div>
    </section>
  );
}

/* ─────────────────────────── Hero ─────────────────────────── */
function Hero() {
  return (
    <section
      className="rounded-3xl overflow-hidden shadow-xl relative"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 65%, #020814 100%)` }}
      data-testid="about-hero"
    >
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full" style={{ background: `${GOLD}18` }} />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full" style={{ background: `${GOLD}0F` }} />
      <div className="relative px-6 lg:px-12 py-10 lg:py-14 text-white">
        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
          <T>About your wallet</T>
        </div>
        <h1 className="font-display text-3xl lg:text-5xl font-semibold mt-2 leading-tight">
          <T>One place to understand your financial life.</T>
        </h1>
        <p className="mt-4 text-white/75 max-w-2xl leading-relaxed">
          <T>The Consumer Trust Wallet isn't a bank account. It's a private space that helps you understand the money decisions being asked of you — quotes, policies, credit, investments — and keeps a plain-language record of everything an institution has verified, offered or explained to you.</T>
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px]">
          <Pill icon={ShieldCheck} label="No product management" />
          <Pill icon={Lock}        label="Your data stays with you" />
          <Pill icon={Scale}       label="Fair-conduct aligned" />
        </div>
      </div>
    </section>
  );
}

function Pill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-white/85 border border-white/15 bg-white/5">
      <Icon className="w-3.5 h-3.5" style={{ color: GOLD }} />
      <span className="tracking-wide"><T>{label}</T></span>
    </span>
  );
}

/* ────────────────────── What it does ────────────────────── */
function WhatItDoes() {
  const features = [
    {
      icon: WalletIcon,
      title: "See what's active, what's being considered",
      body: "The Home page shows every product you already have, every quote you're weighing up and every review you started but never finished — no balances, just clarity.",
    },
    {
      icon: FileText,
      title: "Keep every document in one vault",
      body: "Upload quotes, policies, wills, retirement statements — anything you're asked to sign. The wallet reads each document and explains it back to you in plain language.",
    },
    {
      icon: Users,
      title: "Fill your Financial Profile once",
      body: "Five short questions about your situation. Every quote and every affordability check in the wallet then uses those facts to tell you honestly if a product fits.",
    },
    {
      icon: Send,
      title: "Request a quote from a trusted advisor",
      body: "Send a magic drop-off link to a specific advisor. When they reply, everything lives in your wallet — never in a WhatsApp thread you'll lose.",
    },
    {
      icon: Building2,
      title: "Your trusted institutions, in one list",
      body: "The banks, insurers and advisors you have actually verified. No phishing links, no unknown callers — if it's not in this list, treat it with suspicion.",
    },
    {
      icon: ShieldCheck,
      title: "Digital safety, always a tap away",
      body: "Scam alerts, a URL/message verifier, and an emergency escalation path when something feels wrong.",
    },
    {
      icon: Compass,
      title: "Life journeys that actually help",
      body: "First home, retirement, starting a business, losing a spouse — guided experiences that walk you through the money side of real life events.",
    },
    {
      icon: ClipboardList,
      title: "A single record of every decision",
      body: "Every quote you accepted or declined, every advisor conversation, every disclosure you were shown — timestamped and hash-chained so nothing can be quietly rewritten.",
    },
  ];

  return (
    <section data-testid="about-features">
      <SectionHeader
        eyebrow="Plain language"
        title="What the wallet actually does"
        hint="No jargon. Here's every meaningful thing you can do from your wallet today."
      />
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((f) => (
          <FeatureCard key={f.title} {...f} />
        ))}
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition p-5 flex gap-4"
      data-testid={`about-feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
        <Icon className="w-5 h-5" style={{ color: NAVY }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}><T>{title}</T></div>
        <p className="text-[13px] text-slate-600 mt-1 leading-relaxed"><T>{body}</T></p>
      </div>
    </div>
  );
}

/* ───────────────────── How it protects ─────────────────── */
function HowItProtects() {
  const rows = [
    {
      icon: Lock,
      title: "Your consent, every time",
      body: "Nothing is shared with an institution unless you say yes. Every share is a revocable credential — you can pull it back at any moment and see who has ever asked.",
    },
    {
      icon: EyeOff,
      title: "Minimum data, always",
      body: "When an institution asks 'is this person over 18?', the wallet answers yes or no — it doesn't hand over your ID number, your address or your date of birth.",
    },
    {
      icon: ScrollText,
      title: "Hash-chained evidence",
      body: "Every entry — a quote accepted, a document uploaded, a disclosure shown — is chained to the previous one. If someone tried to alter history you'd see the break instantly.",
    },
    {
      icon: Ban,
      title: "Zero phishing surface",
      body: "The wallet only accepts messages from your verified institutions list. Random 'Hi, I'm from your bank' texts have nowhere to land.",
    },
    {
      icon: AlertTriangle,
      title: "Fair-conduct nudges",
      body: "The wallet actively watches for gaps that hurt you — a stale will, expired risk cover, a missing beneficiary — and prompts you to review, not to buy.",
    },
    {
      icon: Handshake,
      title: "One side of the table",
      body: "The wallet does not earn commission and does not sell products. Its job is to help you understand what's being offered to you — nothing else.",
    },
  ];
  return (
    <section data-testid="about-protection">
      <SectionHeader
        eyebrow="How it protects you"
        title="Built for consumers, not for banks"
        hint="Every design choice is on your side of the table."
      />
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <ProtectionRow key={r.title} {...r} />
        ))}
      </div>
    </section>
  );
}

function ProtectionRow({ icon: Icon, title, body }) {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100 p-5 flex gap-4"
      data-testid={`about-protect-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: NAVY }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}><T>{title}</T></div>
        <p className="text-[13px] text-slate-600 mt-1 leading-relaxed"><T>{body}</T></p>
      </div>
    </div>
  );
}

/* ───────────── Fair Conduct + TCF (6 outcomes) ─────────── */
function FairConductTCF() {
  const outcomes = [
    {
      n: "1",
      title: "You can trust that fair treatment is central to their culture",
      body: "The institutions in your wallet are held to a standard where treating you fairly isn't a slogan — it's measured, monitored and reported to the regulator (FSCA).",
    },
    {
      n: "2",
      title: "Products are designed for people like you",
      body: "A product should only be offered to you if it was designed for someone in your situation — your age, your income, your needs. If it wasn't, it shouldn't be sold to you.",
    },
    {
      n: "3",
      title: "You're given the information you need, when you need it",
      body: "Clear, plain-language disclosures before you sign — not buried in a 40-page policy document you'll never read.",
    },
    {
      n: "4",
      title: "The advice you get suits your circumstances",
      body: "Any recommendation must be based on your actual financial situation. That's why your Financial Profile lives in the wallet — so no one can advise you blindly.",
    },
    {
      n: "5",
      title: "The product performs the way you were told it would",
      body: "Returns, fees, cover, waiting periods — what you were promised must match what you get. The wallet keeps the evidence so you can prove it if it doesn't.",
    },
    {
      n: "6",
      title: "You can complain, switch or leave without unfair barriers",
      body: "Cancelling a product, switching provider or raising a complaint should be as easy as it was to sign up. The wallet keeps a receipt of every step.",
    },
  ];

  return (
    <section data-testid="about-tcf">
      <SectionHeader
        eyebrow="Fair Conduct · TCF"
        title="What 'Treating Customers Fairly' really means"
        hint="South African financial law (COFI · FSCA · FAIS) sets six outcomes that every institution owes you. The wallet is built around them."
      />

      <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-6 lg:p-7">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
            <BookOpen className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <p className="text-[13.5px] text-slate-700 leading-relaxed">
            <strong style={{ color: NAVY }}>Fair Conduct</strong> is the umbrella
            principle in South Africa's <em>Conduct of Financial Institutions Act
            (COFI)</em>: every regulated institution — from your bank to your
            broker — has a legal duty to put your outcomes first. The regulator
            (<strong>FSCA</strong>) supervises this through six specific
            outcomes, together known as <strong>Treating Customers Fairly
            (TCF)</strong>. Here's what each one means, in the words you deserve
            to hear them in.
          </p>
        </div>

        <ol className="mt-6 space-y-4">
          {outcomes.map((o) => (
            <TCFOutcome key={o.n} {...o} />
          ))}
        </ol>

        <div className="mt-6 rounded-xl p-4 border border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="about-tcf-callout">
          <NaviFace size="sm" />
          <p className="text-[12.5px] text-amber-900 leading-relaxed">
            <strong style={{ color: NAVY }}>NAVI</strong> — your in-wallet assistant — can help you here.
            If anything in your wallet feels like it breaks one of these
            outcomes — a fee you weren't told about, advice that clearly
            doesn't fit you, a product that isn't performing — she can
            help you package the evidence and raise it, in one tap.
          </p>
        </div>
      </div>
    </section>
  );
}

function TCFOutcome({ n, title, body }) {
  return (
    <li className="flex gap-4" data-testid={`about-tcf-outcome-${n}`}>
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center font-display font-semibold text-white flex-shrink-0"
        style={{ background: NAVY }}
      >
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[14.5px] font-semibold" style={{ color: NAVY }}><T>{title}</T></div>
        <p className="text-[13px] text-slate-600 mt-1 leading-relaxed"><T>{body}</T></p>
      </div>
    </li>
  );
}

/* ────────────────── What it is NOT ────────────────── */
function WhatItIsNot() {
  const items = [
    "A bank account — it does not hold your money.",
    "A trading app — it does not buy or sell anything.",
    "A robo-advisor — it does not tell you what to do.",
    "A data-mining service — it does not sell your information.",
  ];
  return (
    <section data-testid="about-what-it-is-not">
      <SectionHeader
        eyebrow="Setting expectations"
        title="What the wallet is not"
      />
      <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((line, i) => (
          <div key={i} className="flex items-start gap-3">
            <Ban className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
            <span className="text-[13px] text-slate-700"><T>{line}</T></span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────── Closing note ─────────────── */
function ClosingNote() {
  return (
    <section
      className="rounded-2xl p-6 lg:p-8 border border-slate-100"
      style={{ background: `${GOLD}0E` }}
      data-testid="about-closing"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: NAVY }} />
        <div>
          <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>
            <T>The one promise</T>
          </div>
          <p className="text-[13.5px] text-slate-700 mt-1 leading-relaxed max-w-3xl">
            <T>The wallet exists so that no South African has to make a money decision they don't understand. If we ever ask you to act, it is only ever in your interest — never in a product provider's.</T>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Shared section header ─────────────── */
function SectionHeader({ eyebrow, title, hint }) {
  return (
    <div>
      {eyebrow && (
        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
          <T>{eyebrow}</T>
        </div>
      )}
      <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1.5" style={{ color: NAVY }}><T>{title}</T></h2>
      {hint && <p className="text-sm text-slate-600 mt-1.5 max-w-2xl leading-relaxed"><T>{hint}</T></p>}
    </div>
  );
}
